import { useMemo, useState } from "react";
import { Mail, Phone, Globe, MapPin, Send, Navigation } from "lucide-react";
import emailjs from "@emailjs/browser";
import Reveal from "../components/Reveal";
import { PageHero } from "../components/Shared";
import { useSettings } from "../context/SettingsContext";
import SEO from "../components/SEO";

export default function Contact() {
  const settings = useSettings();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const CONTACTS = useMemo(() => [
    settings.email && { ic: Mail, k: "Email", v: settings.email, href: `mailto:${settings.email}` },
    settings.website && { ic: Globe, k: "Website", v: settings.website.replace(/^https?:\/\//, ""), href: settings.website.startsWith("http") ? settings.website : `https://${settings.website}` },
    settings.address && { ic: MapPin, k: "Location", v: settings.address, href: null },
  ].filter(Boolean), [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSent(false);
    setBusy(true);

    const formData = new FormData(e.target);
    const name = formData.get("name");
    const phone = formData.get("phone");
    const email = formData.get("email");
    const message = formData.get("message");

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      console.warn("EmailJS environment variables are not configured.");
      setErr("Failed to send message: EmailJS service is not configured.");
      setBusy(false);
      return;
    }

    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          subject: `${name} - Lead from Website`,
          name,
          phone,
          email: email || "N/A",
          message,
        },
        publicKey
      );

      setSent(true);
      e.target.reset();
      setTimeout(() => setSent(false), 5000);
    } catch (error) {
      console.error("EmailJS error:", error);
      setErr("Failed to send message. Please try again later.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact | SUV Engineers"
        description="Get in touch with SUV Engineers. Contact our team in Bengaluru for electrical contracting enquiries, quotes, and project discussions."
      />
      <PageHero crumb="Contact" title="Talk to SUV Engineers about your requirement.">
        Share your site requirement and we will personally review it to give you accurate scope
        and timelines.
      </PageHero>

      <section className="section muted">
        <div className="section-inner">
          <div className="contact-grid">
            <Reveal>
              <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
                {sent && (
                  <div className="form-success" data-testid="contact-success">
                    Thank you! Your enquiry has been noted — we'll be in touch shortly.
                  </div>
                )}
                {err && (
                  <div className="login-error" style={{ marginBottom: 16 }}>
                    {err}
                  </div>
                )}
                <label>
                  Name
                  <input type="text" name="name" placeholder="Your name" required data-testid="contact-name" />
                </label>
                <label>
                  Phone
                  <input type="tel" name="phone" placeholder="+91" required data-testid="contact-phone" />
                </label>
                <label>
                  Email
                  <input type="email" name="email" placeholder="you@company.com" data-testid="contact-email" />
                </label>
                <label>
                  Requirement
                  <textarea name="message" rows="5" placeholder="HT/LT installation, networking, cabling…" required data-testid="contact-message" />
                </label>
                <button type="submit" className="btn btn-primary" data-testid="contact-submit" disabled={busy}>
                  {busy ? "Sending…" : "Send Enquiry"} <Send size={17} />
                </button>
                <p className="form-note">We typically respond within one business day.</p>
              </form>
            </Reveal>

            <Reveal delay={120}>
              <aside className="contact-panel">
                <h2>Contact details</h2>
                {CONTACTS.map((c) => (
                  <div className="contact-row" key={c.k}>
                    <span className="ic"><c.ic /></span>
                    <div>
                      <div className="k">{c.k}</div>
                      <div className="v">
                        {c.href ? <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{c.v}</a> : c.v}
                      </div>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 22 }}>
                  <div className="k" style={{ fontSize: "0.78rem", color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                    Speak to our team
                  </div>
                  {[
                    { n: "Manish Udupa", r: "Admin & Commercial", t: "+919108027356", p: "+91 91080 27356" },
                  ].map((m) => (
                    <div className="contact-row" key={m.t}>
                      <span className="ic"><Phone /></span>
                      <div>
                        <div className="v">{m.n} <span style={{ color: "var(--muted)", fontWeight: 600 }}>· {m.r}</span></div>
                        <div className="k" style={{ textTransform: "none", letterSpacing: 0, fontSize: "0.9rem", marginTop: 2 }}>
                          <a href={`tel:${m.t}`}>{m.p}</a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </aside>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section map-section" style={{ paddingTop: 0 }}>
        <div className="section-inner">
          <Reveal className="section-head">
            <p className="eyebrow">Find Us</p>
            <h2 style={{ fontSize: "clamp(1.8rem,3.4vw,2.6rem)" }}>Visit our office in Bengaluru.</h2>
          </Reveal>
          <Reveal className="map-card" data-testid="map-card">
            <div className="map-frame">
              <iframe
                title="SUV Engineers office location"
                src="https://maps.google.com/maps?q=12.9291779,77.5499944(SUV%20Engineers)&z=16&output=embed"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="map-bar">
              <div className="mb-loc">
                <span className="mb-ic"><MapPin /></span>
                <div>
                  <div className="mb-name">SUV Engineers</div>
                  <div className="mb-sub">Bengaluru, Karnataka, India</div>
                </div>
              </div>
              <a
                className="btn btn-primary btn-sm"
                href="https://maps.app.goo.gl/uy56VULybUWfjXCE8"
                target="_blank"
                rel="noreferrer"
                data-testid="directions-btn"
              >
                <Navigation size={16} /> Get Directions
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
